import { locationApi, type DeletedLocation } from '@/api/account-management/locationApi';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import Box from '@mui/material/Box';
import { DataGrid, GridActionsCellItem, type GridColDef, type GridRowParams } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactElement } from 'react';
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { Perm } from '../../permissions/permissions';
import { usePermissions } from '../../permissions/usePermissions';
import { fillHeight } from '../../theme';
import DataGridDeleteButton from '../common/data-grid/DataGridDeleteButton';
import { useToast } from '../common/toast/ToastProvider';

// Admin-only screen: soft-deleted locations for the selected brand, with restore + permanent-delete.
const DeletedLocations = (): ReactElement => {
    const qc = useQueryClient();
    const toast = useToast();
    const strings = useLocalizedStrings();
    const app = useAppContext();
    const perms = usePermissions();

    // The route guard only decides whether the page opens at all, which is the `read` half. The two actions
    // on it are separately grantable and were not checked, so anyone who could see the list could also
    // restore and permanently destroy from it.
    //
    // Restore maps to `update` — it returns an existing record to service. Permanent delete maps to `delete`,
    // and is the one that actually destroys data. `create` has no action here: nothing on this screen makes
    // a new location, so that level is deliberately unused rather than mapped to something approximate.
    const canRestore = perms.canUpdate(Perm.adminDeletedLocations);
    const canHardDelete = perms.canDelete(Perm.adminDeletedLocations);

    const brandId = app.brandId ?? undefined;

    const { data: deleted, isFetching } = useQuery<DeletedLocation[]>({
        queryKey: ['deleted-locations', brandId ?? ''],
        enabled: !!brandId,
        queryFn: () => locationApi.listDeleted(brandId!),
        staleTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: false,
    });

    const { mutateAsync: restore } = useMutation({
        mutationFn: locationApi.restore,
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.deletedLocationsRestoreSuccess });
            await qc.invalidateQueries({ queryKey: ['deleted-locations', brandId] });
            // Restored location returns to the normal lists.
            await qc.invalidateQueries({ queryKey: ['locations', brandId] });
        },
        onError: () => toast({ severity: 'error', message: strings.deletedLocationsActionError }),
    });

    const { mutateAsync: hardDelete } = useMutation({
        mutationFn: locationApi.hardDelete,
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.deletedLocationsHardDeleteSuccess });
            await qc.invalidateQueries({ queryKey: ['deleted-locations', brandId] });
        },
        onError: () => toast({ severity: 'error', message: strings.deletedLocationsActionError }),
    });

    const getHardDeleteWarning = (id: string) => {
        const loc = deleted?.find((d) => d.id === id);
        return strings.deletedLocationsConfirmHardDelete.replace('{{name}}', loc?.name ?? '');
    };

    const columns: GridColDef<DeletedLocation>[] = [
        { field: 'name', headerName: strings.name, flex: 2 },
        {
            field: 'deletedAt',
            headerName: strings.deletedLocationsDeletedAt,
            flex: 1.5,
            renderCell: (params) => (params.row.deletedAt ? new Date(params.row.deletedAt).toLocaleString() : ''),
        },
        { field: 'deletedBy', headerName: strings.deletedLocationsDeletedBy, flex: 1.5 },
        {
            field: 'actions',
            headerName: strings.actions,
            type: 'actions',
            flex: 1.2,
            // Each action is dropped entirely rather than disabled, matching how the other grids hide what a
            // role may not do (see RoleList / UserList).
            getActions: (params: GridRowParams) => [
                ...(canRestore
                    ? [
                          <GridActionsCellItem
                              key="restore"
                              icon={<RestoreFromTrashIcon />}
                              label={strings.deletedLocationsRestore}
                              onClick={() => restore(params.id.toString())}
                          />,
                      ]
                    : []),
                <DataGridDeleteButton
                    key="hard-delete"
                    id={params.id.toString()}
                    getWarningMessage={getHardDeleteWarning}
                    onClick={(id) => hardDelete(id)}
                    aria-label="permanently delete location"
                    hidden={!canHardDelete}
                />,
            ],
        },
    ];

    return (
        <Box sx={{ ...fillHeight, overflow: 'hidden', p: 2 }}>
            <Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
                <DataGrid
                    rows={deleted ?? []}
                    columns={columns}
                    autoPageSize
                    loading={!brandId ? false : isFetching}
                    localeText={{ noRowsLabel: !brandId ? strings.selectBrand : strings.deletedLocationsNone }}
                    disableRowSelectionOnClick
                />
            </Box>
        </Box>
    );
};

export default DeletedLocations;
